<template>
  <div class="picker" :class="{ 'picker-disabled': disabled }" style="display: inline-block">
    <div
      v-if="!asInput"
      v-click-outside="closeDatetimePicker"
      class="inline pointer"
      style="position: relative"
    >
      <BasePopper :display="show" :arrow="false" default-slot-is-inline animate :strategy="'fixed'">
        <div @click.prevent="triggerDatetimePicker">
          <slot :value="displayValue">
            {{ displayValue === '' ? placeholder : displayValue }}
          </slot>
        </div>
        <template #popper>
          <div ref="date"></div>
        </template>
      </BasePopper>
      <input v-if="!asInput" :value="displayValue" :name="name" type="hidden" />
    </div>

    <template v-if="asInput">
      <div :class="{ 'floatlabel floatlabel-active': floatLabel, 'input-group': !floatLabel }">
        <label v-if="floatLabel" :for="name" class="control-label">{{ label }}</label>
        <span v-if="!floatLabel && !noIcon" class="input-group-addon">
          <i class="zmdi zmdi-calendar" />
        </span>
        <div class="dtp-container">
          <input
            ref="date"
            :value="displayValue"
            :placeholder="placeholder"
            :disabled="disabled"
            :name="name"
            :required="required"
            type="text"
            class="datepicker-input form-control f-14"
            style="width: 100%"
            :data-test="dataTest"
            :class="{
              required: required,
              'input-error': !!error,
            }"
            autocomplete="off"
            @blur="$emit('blur')"
          />
          <input v-if="!asInput" :value="displayValue" :name="name" type="hidden" />
        </div>
      </div>
      <div v-if="error && error.length" class="tw-text-red-500 tw-mt-1">{{ error }}</div>
    </template>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import BasePopper from '@/components/BasePopper.vue'
import FunctionZipMixin from '@/mixins/MethodFZipMixin'
import Datepicker from '@/utils/forms/Datepicker'
/**
 * Provide a datetime picker as an input, or when clicking on any slot content if :as-input="false"
 * It works as our wrapper around the vanillajs datepicker library which documentation can be found
 * here https://mymth.github.io/vanillajs-datepicker/#/
 */
export default {
  components: { BasePopper },
  mixins: [FunctionZipMixin],
  props: {
    // If this component should work as a type of input. True if this is used with blade or forms
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    asInput: { type: Boolean, default: true },
    modelValue: { type: String, default: '' },
    // The name of the input used in conjunction with form submitting
    name: { type: String, default: 'date' },
    format: { type: String, default: 'DD/MM/YYYY' },
    placeholder: { type: String, default: 'Select date' },
    disabled: { type: Boolean, default: false },
    // Set this to true if we don't want to display input with icon
    noIcon: { type: Boolean, default: false },
    // Label used in case of asInput == true
    label: { type: String, default: 'Select date' },
    // If the label floats inside the input space
    floatLabel: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    // The maximum date allowed to select
    maxDate: { type: String, default: '' },
    // The minimum date allowed to select
    minDate: { type: String, default: '' },
    error: { type: [Boolean, String], default: false },
    dataTest: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'hide', 'show', 'blur'],
  data() {
    return {
      picker: null,
      displayValHidden: this.modelValue,
      show: false,
    }
  },
  computed: {
    // Used for watch purposes
    dateLimits() {
      return { min: this.minDate, max: this.maxDate }
    },
    // The value used when displaying the current date
    displayValue: {
      get() {
        return this.displayValHidden
      },
      set() {
        this.$emit('update:modelValue', this.displayValue)
      },
    },
  },
  watch: {
    dateLimits: {
      deep: true,
      immediate: true,
      handler({ min, max }) {
        nextTick(() => {
          this.picker?.setOptions({
            minDate: min || '',
            maxDate: max || '',
          })
          if (this.parse(min) > this.parse(this.displayValue)) {
            this.picker?.setDate(min)
          }
          if (this.parse(max) < this.parse(this.displayValue)) {
            this.picker?.setDate(max)
          }
        })
      },
    },
    modelValue(v) {
      if (v !== this.displayValue) {
        this.picker?.setDate(v)
        this.displayValHidden =
          Datepicker.formatDate(this.picker.getDate(), this.format.toLowerCase()) ?? this.modelValue
      }
    },
  },
  mounted() {
    const includesDays = this.format.toLowerCase().includes('dd')
    this.picker = new Datepicker(this.$refs.date, {
      // We lowercase the format as its uppercase formatting is weird and the lowercase formatting works like moment
      format: this.format.toLowerCase(),
      language: currentUser?.locale ?? 'en',
      autohide: true,
      pickLevel: includesDays ? 0 : 1,
      orientation: 'bottom auto',
      maxView: 2,
      maxDate: this.maxDate,
      minDate: this.minDate,
      prevArrow: `<span
                    class="glyphicon glyphicon-arrow-left"
                    title="${$t('Previous Year')}">
                  </span>`,
      nextArrow: `<span
                    class="glyphicon glyphicon-arrow-right"
                    title="${$t('Next Year')}">
                  </span>`,
    })
    this.picker.setDate(this.modelValue)
    this.attachListenerToCalendarChanges()
  },
  methods: {
    /**
     * This method will attach all the needed event listeners that are on the DatePicker
     * see here https://mymth.github.io/vanillajs-datepicker/#/api?id=events for the details
     * @affects
     *  this.$refs.date
     * @return
     */
    attachListenerToCalendarChanges() {
      // Handle event from the underlying library
      const update = () => {
        this.$emit(
          'update:modelValue',
          Datepicker.formatDate(this.picker.getDate(), this.format.toLowerCase()),
        )
      }
      this.$refs.date.addEventListener('changeDate', this.fZipArg(update, this.closeDatetimePicker))
      this.$refs.date.addEventListener('changeMonth', update)
      this.$refs.date.addEventListener('changeYear', update)
      this.$refs.date.addEventListener('hide', () => {
        this.$emit('hide')
      })
      this.$refs.date.addEventListener('show', () => {
        this.$emit('show')
      })
    },
    /**
     * Triggers the datetime picker to be displayed
     * @affects
     *  this.show
     * @return
     */
    triggerDatetimePicker() {
      this.show = true
      this.$emit('show')
    },
    /**
     * Closes the datetime picker to be displayed
     * @affects
     *  this.show
     * @return
     */
    closeDatetimePicker() {
      if (this.show) {
        this.$emit('hide')
      }
      this.show = false
    },
    /**
     * @description
     *  Simply shorthand to parse a date with the Datepicker library
     * @param date
     *  The date can be in any accepted date form as long as it matches the format
     * @returns {Number}
     */
    parse(date) {
      return Datepicker.parseDate(date, this.format.toLowerCase())
    },
  },
}
</script>

<style lang="scss">
@import 'node_modules/vanillajs-datepicker/sass/mixins';
@import 'node_modules/vanillajs-datepicker/sass/datepicker';
@import 'resources/sass/frontend/_color-variables';

.picker-disabled {
  background: #e6e8ec !important;
}

.picker {
  padding: 0px;
  .datepicker-input.form-control {
    border-radius: 0 4px 4px 0 !important;
  }

  .datepicker-dropdown {
    z-index: 2000;
  }

  .datepicker-picker {
    font-size: 14px;
  }

  .datepicker-header {
    padding: 0.875rem 0;
    background-color: $gray-900;
    .datepicker-controls {
      .button,
      .button:hover {
        font-size: 15px;
        text-transform: uppercase;
        color: white;
        background-color: $gray-900;
        font-weight: normal !important;
        &:focus {
          border: none !important;
          box-shadow: none !important;
        }
      }
    }
    .glyphicon {
      font-size: 0.625rem;
    }
  }

  .datepicker-main {
    padding: 0;

    .days {
      .days-of-week {
        text-transform: uppercase;
        background: $gray-800;
        color: white;
        .dow {
          font-weight: 600;
          height: 1.25rem;
          line-height: 1.25rem;
          font-size: 0.625rem;
        }
      }
      .datepicker-grid {
        padding: 0 5px 5px 5px;
        .datepicker-cell {
          height: 2.475em;
          line-height: 2.475em;
        }
      }
    }
    .datepicker-grid {
      margin-top: 5px;
      padding: 0 1.5em 1em;
      .datepicker-cell {
        height: 3.75em;
        line-height: 3.75em;
      }

      .datepicker-cell:hover {
        background-color: $indigo-200 !important;
        color: $gray-900;
      }

      .datepicker-cell.focused,
      .datepicker-cell.focused:hover,
      .datepicker-cell.selected,
      .datepicker-cell.selected:hover {
        background-color: $gray-900 !important;
        color: white;
      }
      span {
        border-radius: 50%;
      }
    }
  }

  .input-error:not(:focus) {
    border-color: $red-500 !important;
  }
}
</style>
